import React, { useState, useEffect, useRef, useMemo } from "react";
import { Row, Col, Breadcrumb, Alert, Container, Card } from "react-bootstrap";
import { Button } from "primereact/button";
import "../assets/css/Shopdetails.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { PiCrownSimpleDuotone, PiCoinsDuotone } from "react-icons/pi";
import { useNavigate, useParams } from "react-router-dom";
import { FiEye, FiFile, FiArchive } from "react-icons/fi";
import { TbWorldDownload, TbBrandAdobeIllustrator } from "react-icons/tb";
import { FaBarcode } from "react-icons/fa";
import { BsFiletypePng, BsFiletypePdf, BsFiletypePsd } from "react-icons/bs";
import { SlVector } from "react-icons/sl";
import { useAuth } from "../context/AuthContext";
import api from "../api/axios";
import useGuestExploreGuard from "../hooks/useGuestExploreGuard";
import GuestExploreOverlay from "../components/GuestExploreOverlay";
import { Skeleton } from "primereact/skeleton";
import { toast } from "react-toastify";
import SEO from "../components/SEO";
import { Rating } from "primereact/rating";
import { InputTextarea } from "primereact/inputtextarea";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
import { slugify } from "../utils/slugify";
const SHOP_STATE_KEY = "shop_state_v1";

const SHOP_DEFAULT_STATE = {
  category: { category_name: "", category_id: 0 },
  subcategory: null,
  searchTerm: "",
  sortBy: "name_asc",
  first: 0,
  rows: 21,
  occasionId: null,
};


const ProtectedCanvasImage = ({
  src,
  alt = "",
  onClick,
  onLoad,
  onError,
  height = 220,
  objectFit = "cover",
  objectPosition = "top center",
  className = "",
  style = {},
}) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);

  const drawImageToCanvas = () => {
    const canvas = canvasRef.current;
    const image = imageRef.current;

    if (!canvas || !image) return;

    const rect = canvas.getBoundingClientRect();

    const displayWidth = Math.max(
      rect.width || canvas.clientWidth || 1,
      1
    );

    const fallbackHeight =
      typeof height === "number"
        ? height
        : parseFloat(height) || 220;

    const displayHeight = Math.max(
      rect.height || canvas.clientHeight || fallbackHeight,
      1
    );

    const pixelRatio = Math.min(
      window.devicePixelRatio || 1,
      2
    );

    canvas.width = Math.round(
      displayWidth * pixelRatio
    );

    canvas.height = Math.round(
      displayHeight * pixelRatio
    );

    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    ctx.setTransform(
      pixelRatio,
      0,
      0,
      pixelRatio,
      0,
      0
    );

    ctx.clearRect(
      0,
      0,
      displayWidth,
      displayHeight
    );

    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;

    if (!sourceWidth || !sourceHeight) {
      return;
    }

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (objectFit === "contain") {
      const scale = Math.min(
        displayWidth / sourceWidth,
        displayHeight / sourceHeight
      );

      const drawWidth =
        sourceWidth * scale;

      const drawHeight =
        sourceHeight * scale;

      const dx =
        (displayWidth - drawWidth) / 2;

      const isTop =
        String(objectPosition)
          .toLowerCase()
          .includes("top");

      const dy =
        isTop
          ? 0
          : (displayHeight - drawHeight) / 2;

      ctx.drawImage(
        image,
        dx,
        dy,
        drawWidth,
        drawHeight
      );

      return;
    }

    /*
     * object-fit: cover
     * object-position: top center
     */
    const sourceAspect =
      sourceWidth / sourceHeight;

    const targetAspect =
      displayWidth / displayHeight;

    let sx = 0;
    let sy = 0;
    let sw = sourceWidth;
    let sh = sourceHeight;

    if (sourceAspect > targetAspect) {
      sw =
        sourceHeight *
        targetAspect;

      sx =
        (sourceWidth - sw) / 2;
    } else {
      sh =
        sourceWidth /
        targetAspect;

      const isTop =
        String(objectPosition)
          .toLowerCase()
          .includes("top");

      sy =
        isTop
          ? 0
          : (sourceHeight - sh) / 2;
    }

    ctx.drawImage(
      image,
      sx,
      sy,
      sw,
      sh,
      0,
      0,
      displayWidth,
      displayHeight
    );
  };

  useEffect(() => {
    if (!src) {
      onError?.();
      return;
    }

    let cancelled = false;
    let resizeObserver = null;

    /*
     * IMPORTANT:
     *
     * Do NOT use fetch(src).blob() here.
     *
     * A normal <img> can display a CDN image even when that CDN
     * does not allow browser fetch/XHR through CORS.
     *
     * fetch() was therefore failing for your thumbnails.
     *
     * Loading with new Image() allows the browser to display the
     * cross-origin image and draw it to canvas.
     *
     * We intentionally do NOT set image.crossOrigin. For a
     * cross-origin CDN this also leaves the canvas "tainted",
     * meaning browser scripts cannot export it using toDataURL()
     * or getImageData().
     */
    const image = new Image();
    image.decoding = "async";

    image.onload = () => {
      if (cancelled) return;

      imageRef.current = image;

      requestAnimationFrame(() => {
        drawImageToCanvas();
        onLoad?.();
      });

      if (
        typeof ResizeObserver !== "undefined" &&
        canvasRef.current
      ) {
        resizeObserver =
          new ResizeObserver(() => {
            requestAnimationFrame(
              drawImageToCanvas
            );
          });

        resizeObserver.observe(
          canvasRef.current
        );
      }
    };

    image.onerror = () => {
      if (cancelled) return;

      console.error(
        "Protected canvas image failed to load:",
        src
      );

      onError?.();
    };

    image.src = src;

    return () => {
      cancelled = true;

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      image.onload = null;
      image.onerror = null;
      imageRef.current = null;
    };
  }, [
    src,
    height,
    objectFit,
    objectPosition,
  ]);

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      className={className}
      onClick={onClick}
      onContextMenu={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      onDragStart={(event) => {
        event.preventDefault();
      }}
      style={{
        display: "block",
        width: "100%",
        height:
          typeof height === "number"
            ? `${height}px`
            : height,
        cursor:
          onClick
            ? "pointer"
            : "default",
        userSelect: "none",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
        ...style,
      }}
    />
  );
};


const Productdetails = () => {
  const { tierlevel, isAuthenticated } = useAuth();

  const {
    guestGuardReady,
    verificationFailed,
    captureGuestClick,
    getPageStatus,
  } = useGuestExploreGuard(isAuthenticated, tierlevel);

  const productDetailsGuestStatus =
    getPageStatus("product_details");

  const isProductDetailsLocked =
    productDetailsGuestStatus.locked;

  const { productId, slug } = useParams();
  const navigate = useNavigate();

  const productCache = useRef(new Map());
  const inFlightRequests = useRef(new Map());

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [error, setError] = useState(null);

  const [recommendationSections, setRecommendationSections] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);

  const [productReviews, setProductReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewStatus, setReviewStatus] = useState(null);
  const [reviewStatusLoading, setReviewStatusLoading] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewBody, setReviewBody] = useState("");
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewEditing, setReviewEditing] = useState(false);

  const [downloadCount, setDownloadCount] = useState(0);
  const [downloadCountLoading, setDownloadCountLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);



  const saveShopState = (nextState) => {
    localStorage.setItem(SHOP_STATE_KEY, JSON.stringify(nextState));
    window.dispatchEvent(
      new CustomEvent("shop-state-updated", { detail: nextState })
    );
  };

  const goToShopReset = () => {
    const nextState = { ...SHOP_DEFAULT_STATE };

    saveShopState(nextState);

    navigate("/shop", {
      state: {
        reset: true,
        category: "",
        subcategory: null,
        searchTerm: "",
        occasionId: null,
        sortBy: "name_asc",
      },
    });
  };

  const goToCategory = () => {
    if (!product?.category) return;

    const nextState = {
      ...SHOP_DEFAULT_STATE,
      category: {
        category_name: product.category,
      },
      subcategory: null,
    };

    saveShopState(nextState);

    navigate("/shop", {
      state: {
        category: product.category,
        subcategory: null,
        searchTerm: "",
        reset: false,
      },
    });
  };

  const goToSubcategory = () => {
    if (!product?.category || !product?.subcategory_name) return;

    const nextState = {
      ...SHOP_DEFAULT_STATE,
      category: {
        category_name: product.category,
      },
      subcategory: product.subcategory_name,
    };

    saveShopState(nextState);

    navigate("/shop", {
      state: {
        category: product.category,
        subcategory: product.subcategory_name,
        searchTerm: "",
        reset: false,
      },
    });
  };

  const getFileIcon = (ext = "") => {
    switch (ext.toLowerCase()) {
      case "png":
      case "jpg":
      case "jpeg":
      case "webp":
        return <BsFiletypePng />;
      case "svg":
        return <SlVector />;
      case "psd":
        return <BsFiletypePsd />;
      case "ai":
        return <TbBrandAdobeIllustrator />;
      case "pdf":
        return <BsFiletypePdf />;
      case "zip":
      case "rar":
      case "7z":
        return <FiArchive />;
      default:
        return <FiFile />;
    }
  };

  const currentTierLevel = Number(tierlevel || 0);

  const getDiscountedCreditCost = (baseCreditCost, tierLevel) => {
    const cost = Number(baseCreditCost ?? 0);

    switch (Number(tierLevel)) {
      case 3:
        return Math.round(cost * 0.9);

      case 4:
        return Math.round(cost * 0.85);

      default:
        return Math.round(cost);
    }
  };

  const hasCreditDiscount = currentTierLevel === 3 || currentTierLevel === 4;


  const getTierLabel = (tier) => {
    switch (Number(tier)) {
      case 1:
        return "Basic";
      case 2:
        return "Premium";
      case 3:
        return "Premium +";
      case 4:
        return "Pro";
      default:
        return null;
    }
  };

  const canSelectImage = (img) => {
    if (!isAuthenticated) return false;

    const requiredTier = img?.min_subscription;

    if (
      requiredTier === null ||
      requiredTier === undefined ||
      requiredTier === "" ||
      Number(requiredTier) <= 0
    ) {
      return true;
    }

    return currentTierLevel >= Number(requiredTier);
  };

  useEffect(() => {
    if (!productId) return;

    /*
     * Do not fetch product data for a guest until the backend
     * has verified that the guest is still below the click limit.
     */
    if (
      !isAuthenticated &&
      (!guestGuardReady || isProductDetailsLocked)
    ) {
      return;
    }

    const fetchProduct = async () => {
      const cacheKey = JSON.stringify({
        productId,
        minSubscription: tierlevel ?? 0,
      });

      if (productCache.current.has(cacheKey)) {
        setProduct(productCache.current.get(cacheKey));
        setLoading(false);
        return;
      }

      if (inFlightRequests.current.has(cacheKey)) return;

      setLoading(true);

      const requestPromise = api
        .get(`/ProductRoutes/customer/singleproduct/${productId}`, {
          params: {
            min_subscription: tierlevel ?? 0,
          },
        })
        .then((res) => {
          if (res.data?.success) {
            productCache.current.set(cacheKey, res.data.data);
            setProduct(res.data.data);
          } else {
            setError("Product not found");
          }
        })
        .catch((err) => {
          console.error(err);
          setError(err?.response?.data?.message || "Failed to fetch product");
        })
        .finally(() => {
          setLoading(false);
          inFlightRequests.current.delete(cacheKey);
        });

      inFlightRequests.current.set(cacheKey, requestPromise);
    };

    fetchProduct();
  }, [
    productId,
    tierlevel,
    isAuthenticated,
    guestGuardReady,
    isProductDetailsLocked,
  ]);

  useEffect(() => {
    if (!productId) return;

    if (
      !isAuthenticated &&
      (!guestGuardReady || isProductDetailsLocked)
    ) {
      return;
    }

    let cancelled = false;

    const fetchRecommendations = async () => {
      setRecommendationSections([]);
      setRecommendationsLoading(true);

      try {
        const res = await api.get(
          `/ProductRoutes/customer/similar-products/${productId}`
        );

        if (cancelled) return;

        if (res.data?.success) {
          const sections = Array.isArray(res.data.sections)
            ? res.data.sections
            : [];

          setRecommendationSections(sections);
        } else {
          setRecommendationSections([]);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "product recommendations fetch failed:",
            err
          );

          setRecommendationSections([]);
        }
      } finally {
        if (!cancelled) {
          setRecommendationsLoading(false);
        }
      }
    };

    fetchRecommendations();

    return () => {
      cancelled = true;
    };
  }, [
    productId,
    isAuthenticated,
    guestGuardReady,
    isProductDetailsLocked,
  ]);

  useEffect(() => {
    setSelectedImage(null);
    setError(null);
  }, [productId]);

  useEffect(() => {
    if (!product?.images?.length) {
      setSelectedImage(null);
      return;
    }

    const stillExists = product.images.some(
      (img) => img.product_image_id === selectedImage?.product_image_id
    );

    if (!stillExists) setSelectedImage(null);
  }, [product, selectedImage]);

  useEffect(() => {
    if (
      !isAuthenticated &&
      isProductDetailsLocked
    ) {
      setProduct(null);
      setRecommendationSections([]);
      productCache.current.clear();
      inFlightRequests.current.clear();
    }
  }, [
    isAuthenticated,
    isProductDetailsLocked,
  ]);


  const loadPublicReviews = async () => {
    if (!productId) return;

    setReviewsLoading(true);

    try {
      const res = await api.get(
        `/productReviewRoutes/public/product/${productId}`
      );

      if (res.data?.success) {
        setProductReviews(res.data.data || []);
      }
    } catch (e) {
      console.warn("reviews fetch failed", e);
    } finally {
      setReviewsLoading(false);
    }
  };

  const loadReviewStatus = async () => {
    if (!productId || !isAuthenticated) return;

    setReviewStatusLoading(true);

    try {
      const res = await api.get(
        `/productReviewRoutes/customer/status/${productId}`
      );

      if (res.data?.success) {
        setReviewStatus(res.data.data);

        if (res.data.data?.review) {
          setReviewRating(res.data.data.review.rating);
          setReviewBody(res.data.data.review.body || "");
        } else {
          setReviewRating(5);
          setReviewBody("");
        }
      }
    } catch (e) {
      console.warn("review status failed", e);
      setReviewStatus({ canReview: false, hasReview: false, review: null });
    } finally {
      setReviewStatusLoading(false);
    }
  };

  const loadDownloadCount = async () => {
    if (!productId) return;

    setDownloadCountLoading(true);

    try {
      const res = await api.get(
        `/ProductRoutes/customer/product-download-count/${productId}`
      );

      if (res.data?.success) {
        setDownloadCount(res.data.download_count || 0);
      } else {
        setDownloadCount(0);
      }
    } catch (e) {
      console.warn("download count fetch failed", e);
      setDownloadCount(0);
    } finally {
      setDownloadCountLoading(false);
    }
  };

  useEffect(() => {
    loadDownloadCount();
    loadPublicReviews();
  }, [productId]);

  useEffect(() => {
    if (isAuthenticated && productId) {
      loadReviewStatus();
    } else {
      setReviewStatus(null);
      setReviewRating(5);
      setReviewBody("");
      setReviewEditing(false);
    }
  }, [isAuthenticated, productId]);

  useEffect(() => {
    if (!reviewStatus?.hasReview) {
      setReviewEditing(false);
    }
  }, [reviewStatus?.hasReview]);

  const otherProductReviews = useMemo(() => {
    const mine =
      isAuthenticated &&
        reviewStatus?.hasReview &&
        reviewStatus.review?.product_review_id != null
        ? reviewStatus.review.product_review_id
        : null;

    if (mine == null) return productReviews;

    return productReviews.filter((r) => r.product_review_id !== mine);
  }, [
    productReviews,
    isAuthenticated,
    reviewStatus?.hasReview,
    reviewStatus?.review?.product_review_id,
  ]);

  const submitReview = async () => {
    if (!productId || !isAuthenticated) {
      toast.info("Please log in to leave a review.");
      return;
    }

    if (!reviewStatus?.canReview) {
      toast.warn("Download this product first to unlock reviews.");
      return;
    }

    if (!reviewBody.trim() || reviewBody.trim().length < 10) {
      toast.warn("Please write at least 10 characters.");
      return;
    }

    setReviewSubmitting(true);

    try {
      const res = await api.post("/productReviewRoutes/customer", {
        product_id: Number(productId),
        rating: reviewRating,
        body: reviewBody.trim(),
      });

      if (res.data?.success) {
        toast.success(res.data.message || "Thanks for your review!");
        setReviewEditing(false);
        await loadPublicReviews();
        await loadReviewStatus();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not save review");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const parseDownloadError = async (error) => {
    const responseData = error?.response?.data;

    if (responseData instanceof Blob) {
      try {
        const text = await responseData.text();

        if (!text) {
          return {};
        }

        return JSON.parse(text);
      } catch (parseError) {
        console.error(
          "Could not parse download error response:",
          parseError
        );

        return {};
      }
    }

    return responseData || {};
  };

  const saveDownloadedFile = (
    blobData,
    imageName
  ) => {
    const blob = new Blob(
      [blobData],
      {
        type: "application/zip",
      }
    );

    const downloadUrl =
      window.URL.createObjectURL(blob);

    const link =
      document.createElement("a");

    link.href = downloadUrl;
    link.download =
      `${imageName || "product"}.zip`;

    document.body.appendChild(link);

    link.click();
    link.remove();

    window.URL.revokeObjectURL(
      downloadUrl
    );
  };

  const handleDownload = async (
    imageId,
    imageName,
    confirmRedownload = false
  ) => {
    if (downloadLoading) {
      return;
    }

    if (!imageId) {
      toast.warn(
        "Please select a file to download"
      );

      return;
    }

    setDownloadLoading(true);

    try {
      const response = await api.post(
        `/cpdroutes/download-image/${imageId}`,
        {
          confirm_redownload:
            confirmRedownload,
        },
        {
          responseType: "blob",
        }
      );

      const downloadStatus =
        response.headers[
        "x-download-status"
        ];

      const contentType = String(
        response.headers[
        "content-type"
        ] || ""
      ).toLowerCase();

      const isDownloadFile =
        response.status === 200 &&
        response.data instanceof Blob &&
        response.data.size > 0 &&
        (
          contentType.includes("zip") ||
          contentType.includes(
            "application/octet-stream"
          )
        );

      if (
        downloadStatus !== "success" &&
        !isDownloadFile
      ) {
        throw new Error(
          "The downloaded response is not a valid ZIP file."
        );
      }

      saveDownloadedFile(
        response.data,
        imageName
      );

      const isRedownload =
        response.headers[
        "x-redownload"
        ] === "true";

      toast.success(
        isRedownload
          ? "Product downloaded again successfully."
          : "Download completed successfully."
      );

      window.dispatchEvent(
        new Event("wallet-updated")
      );

      await Promise.all([
        loadReviewStatus(),
        loadDownloadCount(),
      ]);
    } catch (error) {
      console.error(
        "Download failed:",
        error
      );

      const status =
        error?.response?.status;

      const errorData =
        await parseDownloadError(
          error
        );

      /*
       * Product was already downloaded.
       * Show PrimeReact confirmation popup instead
       * of displaying an error toast.
       */
      if (
        status === 409 &&
        (
          errorData?.already_downloaded ||
          errorData?.confirmation_required
        )
      ) {
        const pricing =
          errorData?.pricing || {};

        const previousDownload =
          errorData?.previous_download ||
          {};

        const payableCredits =
          Number(
            pricing.payable_credit_cost ??
            selectedImage?.credit_cost ??
            0
          );

        const baseCredits =
          Number(
            pricing.base_credit_cost ??
            selectedImage?.credit_cost ??
            0
          );

        const discountPercentage =
          Number(
            pricing.discount_percentage ||
            0
          );

        const previousDate =
          previousDownload.downloaded_at
            ? new Date(
              previousDownload.downloaded_at
            ).toLocaleString(
              "en-IN",
              {
                dateStyle: "medium",
                timeStyle: "short",
              }
            )
            : null;

        confirmDialog({
          // header:
          //   "Product already downloaded",
          // headerClassName: "custom-confirm-header",

          // icon:
          //   "pi pi-exclamation-triangle",

          message: (
            <div
              className="d-flex flex-column gap-3"
            >
              <div className="text-light">
                You have already downloaded
                this product.
              </div>

              {previousDate && (
                <div
                  className="text-pink"
                  style={{
                    fontSize: "14px",
                  }}
                >
                  Previously downloaded on{" "}
                  <strong>
                    {previousDate}
                  </strong>
                </div>
              )}

              <div
                className="p-3 rounded"
                style={{
                  background:
                    "rgba(255, 113, 188, 0.08)",
                  border:
                    "1px solid rgba(255, 113, 188, 0.2)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between gap-3">
                  <span className="text-light">
                    Credits that will be
                    deducted
                  </span>

                  <span className="d-flex align-items-center gap-1 text-pink">
                    <PiCoinsDuotone />

                    <strong>
                      {payableCredits}
                    </strong>

                    {discountPercentage > 0 &&
                      baseCredits !==
                      payableCredits && (
                        <del
                          className="text-light"
                          style={{
                            fontSize:
                              "12px",
                          }}
                        >
                          {baseCredits}
                        </del>
                      )}
                  </span>
                </div>

                {discountPercentage >
                  0 && (
                    <small className="text-light">
                      Your{" "}
                      {discountPercentage}%
                      subscription discount
                      has been applied.
                    </small>
                  )}
              </div>

              <small className="text-light">
                Credits will only be
                deducted after you confirm
                the re-download.
              </small>
            </div>
          ),

          acceptLabel:
            `Download Again (${payableCredits} coins)`,

          rejectLabel:
            "Cancel",

          // acceptIcon:
          //   "pi pi-download",

          // rejectIcon:
          //   "pi pi-times",

          acceptClassName:
            "alreadybutton",

          rejectClassName:
            "p-button-text p-button-danger smallcancel",

          closable: true,
          draggable: false,

          accept: () => {
            handleDownload(
              imageId,
              imageName,
              true
            );
          },

          reject: () => {
            toast.info(
              "Re-download cancelled."
            );
          },
        });

        return;
      }

      if (
        status === 403 &&
        errorData?.upgrade_required
      ) {
        toast.warn(
          errorData.message ||
          "Please upgrade your package to download this product."
        );

        return;
      }

      if (status === 400) {
        toast.warn(
          errorData.message ||
          "Insufficient wallet balance."
        );

        return;
      }

      if (status === 401) {
        toast.error(
          "Please log in again to continue."
        );

        return;
      }

      toast.error(
        errorData.message ||
        error.message ||
        "Download failed. Please try again."
      );
    } finally {
      setDownloadLoading(false);
    }
  };

  const renderProductCard = (p) => (
    <SwiperSlide key={p.product_id}>
      <Card
        className="card-click"
        style={{ cursor: "pointer" }}

        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}

        onDragStart={(event) => {
          event.preventDefault();
        }}
      >
        <ProtectedCanvasImage
          src={
            p.thumbnail?.image_url ||
            "/placeholder.png"
          }
          alt={p.product_name}
          height={160}
          objectFit="cover"
          objectPosition="top center"
          onClick={() =>
            navigate(
              `/product/${p.product_id}/${slugify(
                p.product_name
              )}`
            )
          }
        />

        {p.is_featured && (
          <div className="prime_icon">
            <PiCrownSimpleDuotone />
          </div>
        )}

        <div
          className="imageoption"
          onClick={() => navigate(`/product/${p.product_id}/${slugify(p.product_name)}`)}
        >
          <div className="buttonlist">
            <div
              className="view"
              title="View"
              onClick={() => navigate(`/product/${p.product_id}/${slugify(p.product_name)}`)}
            >
              <FiEye />
            </div>
          </div>
        </div>

        <div className="product-title">
          <p onClick={() => navigate(`/product/${p.product_id}/${slugify(p.product_name)}`)}>
            {p.product_name}
          </p>

          <span className="text-pink" style={{ fontSize: "16px" }}>
            <PiCoinsDuotone />{" "}
            {hasCreditDiscount &&
              Number(p.credit_cost ?? 0) !==
              getDiscountedCreditCost(p.credit_cost, currentTierLevel) ? (
              <>
                <b>
                  {getDiscountedCreditCost(
                    p.credit_cost,
                    currentTierLevel
                  )}
                </b>{" "}
                <del style={{ fontSize: "12px", opacity: 0.6 }}>
                  {Math.round(Number(p.credit_cost ?? 0))}
                </del>
              </>
            ) : (
              <>{Math.round(Number(p.credit_cost ?? 0)) || "Free"}</>
            )}
          </span>
        </div>
      </Card>
    </SwiperSlide>
  );

  const renderProductSlider = (
    title,
    items = [],
    loading = false
  ) => {
    if (!loading && (!Array.isArray(items) || items.length === 0)) {
      return null;
    }

    return (
      <Row
        className="mx-0 mt-lg-5 mt-4 justify-content-between"
        style={{
          overflow: "hidden",
        }}
      >
        <h3>
          {title}

          {loading && (
            <i
              className="pi pi-spin pi-spinner ms-2"
              style={{
                fontSize: "1rem",
                color: "#aaa",
              }}
            />
          )}
        </h3>

        <div>
          {loading ? (
            <div className="d-flex gap-3 mt-4">
              {Array.from({
                length: 6,
              }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    minWidth: 160,
                    flex: "0 0 160px",
                  }}
                >
                  <Skeleton
                    width="160px"
                    height="160px"
                    className="dark-skeleton mb-2"
                  />

                  <Skeleton
                    width="130px"
                    height="0.9rem"
                    className="dark-skeleton mb-1"
                  />

                  <Skeleton
                    width="60px"
                    height="0.9rem"
                    className="dark-skeleton"
                  />
                </div>
              ))}
            </div>
          ) : (
            <Swiper
              spaceBetween={20}
              slidesPerView={6}
              navigation
              modules={[Navigation]}
              className="mt-4"
              breakpoints={{
                0: {
                  slidesPerView: 2,
                },
                576: {
                  slidesPerView: 3,
                },
                768: {
                  slidesPerView: 4,
                },
                992: {
                  slidesPerView: 6,
                },
              }}
            >
              {items.map(renderProductCard)}
            </Swiper>
          )}
        </div>
      </Row>
    );
  };

  const toArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch {
        return [value];
      }
    }
    return [];
  };

  const seoKeywords = [
    product?.product_name,
    product?.category,
    product?.subcategory_name,
    ...toArray(product?.keywords),
    ...toArray(product?.tags),
    ...toArray(product?.illustration_style),
    ...toArray(product?.culture),
    ...toArray(product?.event),
    "Shaadi Material",
    "wedding design assets",
    "wedding invitation templates",
    "wedding clipart",
    "wedding monogram",
  ]
    .filter(Boolean)
    .map((item) => String(item).trim())
    .filter(Boolean)
    .filter((item, index, arr) => arr.indexOf(item) === index)
    .join(", ");

  const seoDescription =
    product?.description ||
    `${product?.product_name || "Premium wedding design asset"} from Shaadi Material. Download premium wedding invitation templates, cliparts, logos, illustrations, patterns and design assets.`;

  const seoTitle = product?.product_name
    ? `${product.product_name} | ${product.category || "Wedding Design Asset"}`
    : "Product Details";


  const productJsonLd = product
    ? {
      "@context": "https://schema.org",
      "@type": "CreativeWork",

      "@id": product?.product_name
        ? `https://shaadimaterial.com/product/${productId}/${slugify(product.product_name)}`
        : `https://shaadimaterial.com/product/${productId}`,

      name: product.product_name,
      description: seoDescription,

      url: product?.product_name
        ? `https://shaadimaterial.com/product/${productId}/${slugify(product.product_name)}`
        : `https://shaadimaterial.com/product/${productId}`,

      image:
        product.thumbnail?.image_url ||
        "https://shaadimaterial.com/logo512.png",

      thumbnailUrl:
        product.thumbnail?.image_url ||
        "https://shaadimaterial.com/logo512.png",

      identifier: product.sku,
      sku: product.sku,

      creator: {
        "@type": "Organization",
        name: "Shaadi Material",
        url: "https://shaadimaterial.com",
      },

      publisher: {
        "@type": "Organization",
        name: "Shaadi Material",
        url: "https://shaadimaterial.com",
      },

      provider: {
        "@type": "Organization",
        name: "Shaadi Material",
        url: "https://shaadimaterial.com",
      },

      keywords: seoKeywords,
      genre: product.category,
      about: product.category,

      isAccessibleForFree: false,

      inLanguage: "en",

      license: "https://shaadimaterial.com/terms-and-conditions",

      usageInfo: "https://shaadimaterial.com/terms-and-conditions",

      datePublished: product.createdAt,
      dateModified: product.updatedAt,

      encodingFormat:
        product.images?.length > 0
          ? product.images
            .map((img) => {
              const ext = img.image_ext?.toLowerCase();

              if (ext === "png") return "image/png";
              if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
              if (ext === "webp") return "image/webp";
              if (ext === "svg") return "image/svg+xml";
              if (ext === "pdf") return "application/pdf";
              if (ext === "psd") return "image/vnd.adobe.photoshop";
              if (ext === "ai") return "application/postscript";
              if (ext === "zip") return "application/zip";

              return null;
            })
            .filter(Boolean)
          : undefined,

      associatedMedia:
        product.images?.length > 0
          ? product.images.map((img) => ({
            "@type": "MediaObject",
            name: img.image_name || product.product_name,
            encodingFormat: (() => {
              const ext = img.image_ext?.toLowerCase();

              if (ext === "png") return "image/png";
              if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
              if (ext === "webp") return "image/webp";
              if (ext === "svg") return "image/svg+xml";
              if (ext === "pdf") return "application/pdf";
              if (ext === "psd") return "image/vnd.adobe.photoshop";
              if (ext === "ai") return "application/postscript";
              if (ext === "zip") return "application/zip";

              return undefined;
            })(),
          }))
          : undefined,

      additionalProperty: [
        {
          "@type": "PropertyValue",
          name: "Credit Cost",
          value: product.credit_cost,
        },
        {
          "@type": "PropertyValue",
          name: "Category",
          value: product.category,
        },
        {
          "@type": "PropertyValue",
          name: "Subcategory",
          value: product.subcategory_name,
        },
      ].filter((item) => item.value !== undefined && item.value !== null),
    }
    : null;

  return (
    <>
      <ConfirmDialog />
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={
          product?.product_name
            ? `https://shaadimaterial.com/product/${productId}/${slugify(product.product_name)}`
            : `https://shaadimaterial.com/product/${productId}`
        }
        keywords={seoKeywords}
        image={product?.thumbnail?.image_url || "https://shaadimaterial.com/logo512.png"}
        type="product"
        jsonLd={productJsonLd}
      />

      <div
        onClickCapture={(event) =>
          captureGuestClick(
            event,
            "product_details"
          )
        }
      >
        <Container className="py-4">
        <Row className="mx-0 breadcrumb_part">
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate(isAuthenticated ? "/home" : "/")}>
              Home
            </Breadcrumb.Item>

            <Breadcrumb.Item onClick={goToShopReset}>Shop</Breadcrumb.Item>

            {product?.category && (
              <Breadcrumb.Item onClick={goToCategory}>
                {product.category}
              </Breadcrumb.Item>
            )}

            {product?.subcategory_name && (
              <Breadcrumb.Item onClick={goToSubcategory}>
                {product.subcategory_name}
              </Breadcrumb.Item>
            )}

            <Breadcrumb.Item active className="elipSEC">
              {product?.product_name}
            </Breadcrumb.Item>
          </Breadcrumb>
        </Row>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        <Row className="mx-0 pt-4 pt-lg-5 justify-content-start">
          <Col md={12} lg={4} xs={12} className="product-details">
            <div className="waterihmb_img">
              {loading ? (
                <Skeleton width="100%" height="420px" className="dark-skeleton" />
              ) : (
                product?.thumbnail?.image_url && (
                  <ProtectedCanvasImage
                    src={
                      product.thumbnail.image_url
                    }
                    alt={
                      product.product_name
                    }
                    height="420px"
                    objectFit="contain"
                    objectPosition="top center"
                    style={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                )
              )}
            </div>
          </Col>

          <Col md={12} lg={7} xs={12} className="product-details ps-lg-5">
            {!isAuthenticated && (
              <Alert className="mb-4 py-1 fade alert alert-warning show text-start d-flex justify-content-between align-items-center">
                You are not authorized for enterprise commercial use yet
                <Alert.Link
                  as="button"
                  onClick={() => navigate("/packages")}
                  className="ms-3 btn btn-warning text-black"
                  style={{ width: "140px" }}
                >
                  Upgrade Now
                </Alert.Link>
              </Alert>
            )}

            {loading ? (
              <>
                <Skeleton width="60%" height="2rem" className="mb-3 dark-skeleton" />
                <Skeleton width="100%" height="1rem" className="mb-2 dark-skeleton" />
                <Skeleton width="90%" height="1rem" className="mb-4 dark-skeleton" />
                <Skeleton width="100%" height="120px" />
              </>
            ) : (
              <>
                <h2 className="mb-2">{product?.product_name}</h2>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "20px",
                  }}
                >
                  {/* <FaBarcode  color="#ff71bc" size={20} />
                  <span style={{ fontSize: "14px", color: "#888" }}>
                    {product?.sku}
                  </span> */}
                  <TbWorldDownload color="#ff71bc" size={20} />
                  <span style={{ fontSize: "14px", color: "#888" }}>
                    {downloadCountLoading
                      ? "Loading downloads..."
                      : `${downloadCount} download${downloadCount === 1 ? "" : "s"}`}
                  </span>

                </div>

                <p style={{ width: "90%" }}>{product?.description || ""}</p>

                {product?.images?.length > 0 && (
                  <Row className="mx-0 mt-4">
                    {product.images.map((img, index) => {
                      const isSelected =
                        selectedImage?.product_image_id === img.product_image_id;

                      const isAllowed = canSelectImage(img);
                      const requiredTierLabel = getTierLabel(img.min_subscription);

                      return (
                        <Col
                          key={img.product_image_id || `${img.image_ext}-${index}`}
                          md={2}
                          lg={3}
                          xs={6}
                          className="px-1 mb-2"
                        >
                          <Card
                            className={`imgTyp ${isSelected ? "img-selected" : ""}`}
                            onClick={() => {
                              if (!isAuthenticated) return;

                              if (isAllowed) {
                                setSelectedImage(img);
                              } else {
                                toast.info(
                                  `This file requires the ${requiredTierLabel || "higher"
                                  } plan.`
                                );
                              }
                            }}
                            style={{
                              cursor: isAuthenticated
                                ? isAllowed
                                  ? "pointer"
                                  : "not-allowed"
                                : "default",
                              border: isSelected
                                ? "2px solid #f7b500"
                                : "1px solid #333",
                              opacity: isAuthenticated && !isAllowed ? 0.45 : 1,
                              position: "relative",
                            }}
                          >
                            {img.min_subscription ? (
                              <div
                                style={{
                                  position: "absolute",
                                  bottom: "-10px",
                                  right: "15px",
                                  backgroundColor: "#FF9800",
                                  color: "#000",
                                  borderRadius: "20px",
                                  padding: "1px 6px",
                                  fontSize: "15px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  zIndex: 1,
                                }}
                                title={`Requires ${requiredTierLabel || "higher plan"
                                  }`}
                              >
                                <PiCrownSimpleDuotone />
                                <span
                                  style={{
                                    fontSize: "10px",
                                    fontWeight: "300",
                                    color: "#000",
                                  }}
                                >
                                  {requiredTierLabel || "Premium"}
                                </span>
                              </div>
                            ) : null}

                            <i>{getFileIcon(img.image_ext)}</i>

                            <span className="text-pink d-flex justify-content-between align-items-center flex-column">
                              <strong>{img.image_ext?.toUpperCase()}</strong>
                              <small className="text-muted">
                                <PiCoinsDuotone />

                                {hasCreditDiscount &&
                                  Number(img.credit_cost ?? 0) !==
                                  getDiscountedCreditCost(
                                    img.credit_cost,
                                    currentTierLevel
                                  ) ? (
                                  <span className="d-flex align-items-center gap-1">
                                    <b>
                                      {getDiscountedCreditCost(
                                        img.credit_cost,
                                        currentTierLevel
                                      )}
                                    </b>
                                    <del style={{ fontSize: "12px", opacity: 0.75 }}>
                                      {Math.round(Number(img.credit_cost ?? 0))}
                                    </del>
                                  </span>
                                ) : (
                                  <b>{Math.round(Number(img.credit_cost ?? 0))}</b>
                                )}
                              </small>
                            </span>
                          </Card>
                        </Col>
                      );
                    })}
                  </Row>
                )}

                <div className="d-flex gap-2">
                  {!isAuthenticated ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      label="Login to Download"
                      onClick={() => navigate("/login")}
                    />
                  ) : Number(tierlevel || 0) === 0 ? (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      style={{ fontSize: "22px", width: "340px" }}
                      label="Activate Subscription"
                      onClick={() => navigate("/packages")}
                    />
                  ) : (
                    <Button
                      className="my-4 d-block py-2 button-pink"
                      disabled={
                        downloadLoading ||
                        !selectedImage ||
                        !canSelectImage(selectedImage)
                      }
                      loading={downloadLoading}
                      style={{ fontSize: "22px", width: "340px" }}
                      onClick={() =>
                        handleDownload(
                          selectedImage.product_image_id,
                          selectedImage.image_name
                        )
                      }
                      label={
                        downloadLoading ? (
                          "Preparing download..."
                        ) : selectedImage ? (
                          <span className="d-flex align-items-center justify-content-center gap-2">
                            {getFileIcon(selectedImage.image_ext)}
                            <span>Download</span>
                          </span>
                        ) : (
                          "Select a file to download"
                        )
                      }
                    />
                  )}
                </div>
              </>
            )}
          </Col>

          <Col md={12} lg={12} xs={12} className="product-details">
            <p className="d-flex flex-column gap-2 mb-4 mt-4">
              <b className="text-pink">Info : </b>
              <span>SKU No. : {product?.sku}</span>
              <span>Category : {product?.category}</span>
              {/* {product?.subcategory_name && (
                <span>Subcategory : {product.subcategory_name}</span>
              )} */}

            </p>

            <div>
              <p className="d-flex flex-column gap-2 mb-4">
                <b className="text-pink">Enterprise Authorization : </b>
                <span>1. We do not accept AI-generated content into our visual library</span>
                <span>2. Includes our standard license.</span>
                <span>3. The image will be of much higher resolution than the thumbnail.</span>
                <span>4. PNG will not have any background colour. Colour is only applied for highlighting the element.</span>
              </p>
            </div>
          </Col>
        </Row>

        {recommendationsLoading ? (
          renderProductSlider(
            "Recommended Products",
            [],
            true
          )
        ) : (
          recommendationSections.map((section) => (
            <React.Fragment key={section.key}>
              {renderProductSlider(
                section.title,
                section.data
              )}
            </React.Fragment>
          ))
        )}

        <Row className="mx-0 mt-5 justify-content-between pb-100" id="review-section">
          <Col md={8} className="customer_addreview">
            <h3 className="mb-4 mb-lg-5">Customer reviews</h3>

            {isAuthenticated &&
              (reviewStatusLoading || reviewStatus == null ? (
                <p className="text-secondary small mb-3">
                  Checking review eligibility…
                </p>
              ) : reviewStatus.canReview ? (
                <Card
                  className="mb-4 p-3"
                  style={{ background: "#1a1a1a", borderColor: "#333" }}
                >
                  {reviewStatus.hasReview ? (
                    <>
                      <div className="d-block align-items-center justify-content-between gap-2 mb-2">
                        <span className="text-pink small fw-semibold mb-3">
                          Your review
                        </span>

                        <div className="d-flex align-items-center gap-1">
                          {reviewEditing ? (
                            <>
                              <Button
                                type="button"
                                icon="pi pi-times"
                                size="small"
                                text
                                rounded
                                className="shop-product-review-cancel-icon"
                                onClick={() => {
                                  setReviewEditing(false);
                                  const r = reviewStatus.review;
                                  if (r) {
                                    setReviewRating(r.rating);
                                    setReviewBody(r.body || "");
                                  }
                                }}
                                aria-label="Cancel editing"
                              />

                              <Button
                                type="button"
                                className="shop-product-review-submit"
                                size="small"
                                rounded
                                icon={
                                  reviewSubmitting
                                    ? "pi pi-spin pi-spinner"
                                    : "pi pi-check"
                                }
                                onClick={submitReview}
                                disabled={reviewSubmitting}
                                aria-label="Save review"
                              />
                            </>
                          ) : (
                            <Button
                              type="button"
                              icon="pi pi-pencil"
                              size="small"
                              text
                              rounded
                              className="shop-product-review-edit-icon"
                              onClick={() => setReviewEditing(true)}
                              aria-label="Edit review"
                            />
                          )}
                        </div>
                      </div>

                      {!reviewEditing ? (
                        <>
                          <div className="shop-product-review-rating-wrap mb-2">
                            <Rating
                              value={reviewRating}
                              readOnly
                              cancel={false}
                              stars={5}
                            />
                          </div>

                          <p
                            className="text-white-50 small mb-0"
                            style={{ whiteSpace: "pre-wrap" }}
                          >
                            {reviewBody}
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="shop-product-review-rating-wrap mb-2">
                            <Rating
                              value={reviewRating}
                              onChange={(e) => setReviewRating(e.value)}
                              cancel={false}
                              stars={5}
                            />
                          </div>

                          <InputTextarea
                            value={reviewBody}
                            onChange={(e) => setReviewBody(e.target.value)}
                            rows={4}
                            className="w-100"
                            placeholder="Share what you think..."
                            style={{ resize: "vertical" }}
                          />
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="d-block align-items-center gap-2 mb-2 flex-wrap">
                        <span className="text-pink">Rate Your Experience</span>

                        <div className="shop-product-review-rating-wrap mt-2 mb-4">
                          <Rating
                            value={reviewRating}
                            onChange={(e) => setReviewRating(e.value)}
                            cancel={false}
                            stars={5}
                          />
                        </div>
                      </div>

                      <InputTextarea
                        value={reviewBody}
                        onChange={(e) => setReviewBody(e.target.value)}
                        rows={4}
                        className="w-100"
                        placeholder="Tell us what you loved or what could be better..."
                        style={{ resize: "vertical" }}
                      />

                      <Button
                        className="button-pink mt-3"
                        label={reviewSubmitting ? "Submitting..." : "Submit Review"}
                        icon={
                          reviewSubmitting ? "pi pi-spin pi-spinner" : "pi pi-check"
                        }
                        disabled={reviewSubmitting}
                        onClick={submitReview}
                      />
                    </>
                  )}
                </Card>
              ) : (
                <p className="text-secondary small mb-3">
                  Download this product first to unlock reviews.
                </p>
              ))}

            {reviewsLoading ? (
              <p className="text-secondary">Loading reviews...</p>
            ) : otherProductReviews.length === 0 ? (
              <p className="text-secondary">No reviews yet.</p>
            ) : (
              otherProductReviews.map((review) => (
                <Card
                  key={review.product_review_id}
                  className="mb-3 p-3"
                  style={{ background: "#1a1a1a", borderColor: "#333" }}
                >
                  <div className="shop-product-review-rating-wrap mb-2">
                    <Rating
                      value={review.rating}
                      readOnly
                      cancel={false}
                      stars={5}
                    />
                  </div>

                  <p
                    className="text-white-50 small mb-1"
                    style={{ whiteSpace: "pre-wrap" }}
                  >
                    {review.body}
                  </p>

                  <small className="text-secondary">
                    {review.customer_name || review.username || "Customer"}
                  </small>
                </Card>
              ))
            )}
          </Col>
        </Row>
        </Container>
      </div>

      <GuestExploreOverlay
        visible={
          !isAuthenticated &&
          isProductDetailsLocked
        }
        clickCount={productDetailsGuestStatus.clickCount}
        limit={productDetailsGuestStatus.limit}
        verificationFailed={verificationFailed}
        daily={productDetailsGuestStatus.daily}
        pageLabel={
          productDetailsGuestStatus.reason === "total"
            ? "total"
            : "Product Details"
        }
      />
    </>
  );
};

export default Productdetails;